import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Phone,
  MapPin,
  Share2,
  Info,
  Settings,
  Eye,
} from 'lucide-react';
import { VCardPersonalInfo } from './vcard/VCardPersonalInfo';
import { VCardContactDetails } from './vcard/VCardContactDetails';
import { VCardAddresses } from './vcard/VCardAddresses';
import { VCardSocialMedia } from './vcard/VCardSocialMedia';
import { VCardAdditionalInfo } from './vcard/VCardAdditionalInfo';
import { VCardOptions } from './vcard/VCardOptions';
import { VCardPreview } from './vcard/VCardPreview';
import {
  VCardData,
  defaultVCardData,
  vcardToContentData,
  contentDataToVCard,
  generateVCard,
} from '@/lib/vcard-utils';
import { useAuthStore } from '@/stores/authStore';

interface VCardFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

const STORAGE_KEY = 'qr-vcard-draft';

export function VCardForm({ contentData, onUpdate }: VCardFormProps) {
  const { session } = useAuthStore();
  const userId = session?.user?.id;

  // Initialize vCard data from stored content or default
  const [vcardData, setVcardData] = useState<VCardData>(() => {
    // First try to parse from contentData
    if (contentData.vcardData) {
      return contentDataToVCard(contentData);
    }
    
    // Then try localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    
    return defaultVCardData;
  });

  const [openSections, setOpenSections] = useState<string[]>(['personal']);

  // Save to localStorage and update parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vcardData));
    
    // Convert to content data format for QR store
    const content = vcardToContentData(vcardData);
    
    // Generate the actual vCard content for QR
    const vcardContent = generateVCard(vcardData);
    
    onUpdate({
      ...content,
      vcardContent, // The actual vCard string for QR generation
    });
  }, [vcardData, onUpdate]);

  // Handle partial updates
  const handleChange = useCallback((updates: Partial<VCardData>) => {
    setVcardData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Count items in each section for badges
  const phoneCount = vcardData.phoneNumbers.filter((p) => p.number).length;
  const emailCount = vcardData.emails.filter((e) => e.email).length;
  const addressCount = vcardData.addresses.length;
  const socialCount = [
    vcardData.social.linkedin,
    vcardData.social.twitter,
    vcardData.social.instagram,
    vcardData.social.facebook,
    vcardData.social.github,
    ...vcardData.social.custom.map((c) => c.url),
  ].filter(Boolean).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
        Digital Business Card (vCard)
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Sections */}
        <div className="space-y-4">
          <Accordion
            type="multiple"
            value={openSections}
            onValueChange={setOpenSections}
            className="space-y-2"
          >
            {/* Personal Info */}
            <AccordionItem value="personal" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <span>Personal Information</span>
                  {vcardData.firstName && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {vcardData.firstName} {vcardData.lastName}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardPersonalInfo
                  data={vcardData}
                  onChange={handleChange}
                  userId={userId}
                />
              </AccordionContent>
            </AccordionItem>

            {/* Contact Details */}
            <AccordionItem value="contact" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" />
                  <span>Contact Details</span>
                  {(phoneCount > 0 || emailCount > 0) && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {phoneCount + emailCount} items
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardContactDetails data={vcardData} onChange={handleChange} />
              </AccordionContent>
            </AccordionItem>

            {/* Addresses */}
            <AccordionItem value="addresses" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span>Addresses</span>
                  {addressCount > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {addressCount}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardAddresses data={vcardData} onChange={handleChange} />
              </AccordionContent>
            </AccordionItem>

            {/* Social Media */}
            <AccordionItem value="social" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-primary" />
                  <span>Social Media</span>
                  {socialCount > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {socialCount}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardSocialMedia data={vcardData} onChange={handleChange} />
              </AccordionContent>
            </AccordionItem>

            {/* Additional Info */}
            <AccordionItem value="additional" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-primary" />
                  <span>Additional Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardAdditionalInfo data={vcardData} onChange={handleChange} />
              </AccordionContent>
            </AccordionItem>

            {/* vCard Options */}
            <AccordionItem value="options" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  <span>vCard Options</span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    v{vcardData.vcardVersion}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <VCardOptions data={vcardData} onChange={handleChange} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Preview Panel */}
        <div className="lg:sticky lg:top-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Eye className="h-4 w-4" />
            Contact Card Preview
          </div>
          <VCardPreview data={vcardData} />
        </div>
      </div>
    </motion.div>
  );
}
